import { isEmpty, cloneDeep } from 'lodash';

import { ANALYTIC_TYPE, PERFORMED_ON, UPDATED_CONFIGURATION } from '@/pages/Cameras/analyticSetting/constants';
import {
  AnalyticsSettingsGet,
  AnalyticsType,
  CameraTunnelParams,
  ObjectDetectionConfigurationGet,
} from '@eencloud/eewc-components/src/service/api-types';
import { ref, watch } from 'vue';

import { CameraTunnelResponse } from '@/pages/Cameras/types';
import api from '@eencloud/eewc-components/src/service/api';
import { defineStore } from 'pinia';
import { getUpdatedSettingsArray } from '@/pages/Cameras/analyticSetting/utils';
import { POSTHOG_EVENTS } from '@/constants/posthog';
import { t } from '@/plugins/i18n';
import { useEevaSettingsStore } from './eeva';
import { useBridgesStore } from './bridges';
import { useMessagingStore } from './messaging';
import { useVSPersistedStateStore } from './videoSearchPersistedState';

export const useAnalyticSettingsStore = defineStore('analyticSettings', () => {
  const messagingStore = useMessagingStore();
  const bridgeStore = useBridgesStore();
  const eevaStore = useEevaSettingsStore();
  const videoSearchPersistedStore = useVSPersistedStateStore();

  // the store will have the analyticSettings information for a camera
  const analyticSettings = ref<AnalyticsSettingsGet[]>([]);
  const objectDetectionConfig = ref<ObjectDetectionConfigurationGet>({});
  const updatedAnalyticSettingsArray = ref<UPDATED_CONFIGURATION[]>([]);
  const initialAnalyticSettingsArray = ref<UPDATED_CONFIGURATION[]>([]);
  const loading = ref(false);
  const motionDetectionSelectedRegion = ref(0);
  const selectedMotionDetectionId = ref<number | string>();
  const selectedPersonDetectionId = ref<number | string>();
  const selectedVehicleDetectionId = ref<number | string>();
  const selectedGunDetectionId = ref<number | string>();
  const selectedRegionCommon = ref<number>(0);
  const vehicleDetectionSelectedRegion = ref(0);
  const cameraTunnelResult = ref<CameraTunnelResponse | null>(null);
  const DEFAULT_SELECTED_REGIONS = {
    [ANALYTIC_TYPE.PERSON_DETECTION]: 0,
    [ANALYTIC_TYPE.VEHICLE_DETECTION]: 0,
    [ANALYTIC_TYPE.PPE_DETECTION]: 0,
  };
  const showHiddenAnalytics = ref(false);
  const selectedRegions = ref(DEFAULT_SELECTED_REGIONS);
  const showLPRBridgeAdvanceSettings = ref(false);
  const isSavedLPRBridgeOption = ref<boolean>(false);
  const isSelectedLPRBridgeOption = ref<boolean>(false);
  const showReferenceObject = ref<boolean>(false);
  const referenceObjectType = ref<string>('');
  const referenceObjectPolygon = ref({ width: 0, height: 0 });
  const shouldUpdateMotionRegionID = ref<boolean>(false);
  const settingsRequiringROI = [ANALYTIC_TYPE.OBJECT_INTRUSION, ANALYTIC_TYPE.OBJECT_LOITERING];
  const settingsWithoutROI = ref<string[]>([]);
  const bridgeBandWidth = ref<string | undefined>('');
  const isLoadingBridgeSettingsData = ref<boolean>(false);
  const showReferenceAnalyticsConfig = ref<boolean>(false);
  const shouldShowLPRCloudWarningMessage = ref<boolean>(false);
  const fleetCodeCameraDropdownRect = ref<DOMRectReadOnly | null>(null);
  const isPersonUpdatedFromMotion = ref<boolean>(false);
  const isVehicleUpdatedFromMotion = ref<boolean>(false);

  async function getAnalyticsSettings(
    cameraId: string,
    loadingState = true
  ): Promise<AnalyticsSettingsGet[] | undefined> {
    loading.value = loadingState;
    try {
      const res = await api.getAnalyticsSettings(cameraId);
      analyticSettings.value = res?.results || [];
      return res?.results;
    } catch (error) {
      console.error(error);
    } finally {
      loading.value = false;
    }
  }

  async function bulkUpdateAnalyticSettings(cameraId: string): Promise<boolean> {
    let hasSavedMotionRegions = false;
    const updatedSettingsArray = getUpdatedSettingsArray(
      initialAnalyticSettingsArray.value,
      updatedAnalyticSettingsArray.value
    );
    let modifiedSettings = updatedSettingsArray.map((item) => {
      const settingConfiguration =
        item.selectedConfigurationIndex !== undefined && item.configuration?.length
          ? item.configuration[item.selectedConfigurationIndex]
          : undefined;

      const data = {
        type: item.id as AnalyticsType,
        enabled: item.performedOn !== PERFORMED_ON.DISABLED,
        configuration: settingConfiguration,
      };
      if (data.configuration?.regions?.length) {
        data.configuration?.regions?.forEach((region) => delete region.color);

        if (data.type === ANALYTIC_TYPE.PERSON_DETECTION || data.type === ANALYTIC_TYPE.VEHICLE_DETECTION) {
          data.configuration?.regions?.forEach((region) => {
            hasSavedMotionRegions = true;
          });
        } else if (data.type === ANALYTIC_TYPE.MOTION_DETECTION || data.type === ANALYTIC_TYPE.GUN_DETECTION) {
          data.configuration?.regions?.forEach((region) => {
            if (region?.id?.includes('motion-region-')) {
              delete region.id;
            }
          });
        }
      }

      // TODO: Removing regions key from payload as it is not implemented
      if (data.type === ANALYTIC_TYPE.EEVA_QUERY_EVENT && data.enabled && data.configuration) {
        delete data.configuration?.regions;
        videoSearchPersistedStore.setEevaSearchQueryHistory(data.configuration?.query);
      }

      return data;
    });

    if (hasSavedMotionRegions) {
      modifiedSettings = updateMotionBasedOnPersonAndVehicle(modifiedSettings);
    }

    let isSuccessful = false;
    if (modifiedSettings.length) {
      const eevaSettings = modifiedSettings.find((setting) => setting.type === ANALYTIC_TYPE.EEVA_QUERY_EVENT);

      // Send posthog analytics for eeva updates
      if (!isEmpty(eevaSettings)) {
        const eevaConfiguration = eevaSettings?.configuration;
        eevaStore.captureEventForAnalytics(POSTHOG_EVENTS.USER_UPDATES_EEVA_ANALYTIC_SETTINGS, {
          een_camera_id: cameraId,
          een_eeva_enabled: eevaSettings.enabled,
          een_eeva_event_trigger_value: eevaConfiguration?.eventTriggerValue,
          een_eeva_event_type_in: eevaConfiguration?.eventType__in,
          een_eeva_label: eevaConfiguration?.label,
          een_eeva_query: eevaConfiguration?.query,
          een_eeva_save_changes_on: new Date().toEENAccountTimeStamp(),
          een_eeva_schedule: eevaConfiguration?.schedule,
          een_eeva_triggerMode: eevaConfiguration?.triggerMode,
        });
      }

      const res = await api.bulkUpdateAnalyticsSettings(cameraId, modifiedSettings);
      if (res) {
        isSuccessful = true;
        Object.keys(res)?.forEach((id) => {
          if (res[id].status === 204) {
            shouldUpdateMotionRegionID.value = true;
          } else {
            // To show analytics limit exceeded error message
            if (res[id]?.error?.details[0]?.reason === 'quotaExceeded') {
              // To get limit from the error response
              const limit = res[id]?.error?.message?.match(/\d+/)[0];
              messagingStore.addNotification({
                iconColor: 'negative',
                icon: '$icon_attention',
                title: t('Failed'),
                body: t(
                  'You have reached the limit of {limit} devices that your hardware will support. You will need to upgrade hardware to add more devices',
                  { limit }
                ),
              });
            } else {
              messagingStore.addNotification({
                iconColor: 'negative',
                icon: '$icon_attention',
                title: res[id].error.status,
                body: `${id}: ${res[id].error.message}`,
              });
            }
          }
          if (res[id].status !== 204 && isSuccessful) {
            isSuccessful = false;
          }
        });
      } else if (isSuccessful) {
        isSuccessful = false;
      }
    }
    if (shouldUpdateMotionRegionID.value) {
      reset();
      await getAnalyticsSettings(cameraId);
      isPersonUpdatedFromMotion.value = false;
      isVehicleUpdatedFromMotion.value = false;
    }
    return isSuccessful;
  }

  async function getBridgeBandWidth(bridgeId: string): Promise<void> {
    try {
      isLoadingBridgeSettingsData.value = true;
      const result = await bridgeStore.getBridgeSettings(bridgeId);
      bridgeBandWidth.value = result?.bandwidth.mode;
    } catch (error) {
      console.log(error);
    } finally {
      isLoadingBridgeSettingsData.value = false;
    }
  }

  /**
   *
   * @param modifiedSettings
   * @returns
   *
   * This function updates the motion detection settings based on the person and vehicle detection settings.
   * The person and vehicle detection don't accept the regions on update. It only accepts the motion detection regions on
   * create. So if update we need to update the region from person or vehicle to motion detection.
   */
  const updateMotionBasedOnPersonAndVehicle = (settings: UPDATED_CONFIGURATION[]) => {
    let modifiedSettings = cloneDeep(settings);
    let motionSettings = modifiedSettings.find((setting) => {
      return setting.type === ANALYTIC_TYPE.MOTION_DETECTION;
    });

    if (motionSettings && !motionSettings.enabled) {
      const personSettings = modifiedSettings.find((setting) => {
        return setting.type === ANALYTIC_TYPE.PERSON_DETECTION;
      });

      const vehicleSettings = modifiedSettings.find((setting) => {
        return setting.type === ANALYTIC_TYPE.VEHICLE_DETECTION;
      });

      if (personSettings?.configuration?.regions?.length || vehicleSettings?.configuration?.regions?.length) {
        modifiedSettings = modifiedSettings.filter((setting) => {
          if (setting.type !== ANALYTIC_TYPE.PERSON_DETECTION && setting.type !== ANALYTIC_TYPE.VEHICLE_DETECTION) {
            return setting;
          }
        });
      }

      motionSettings.configuration = [];
    } else {
      let motionObject = motionSettings;
      if (!motionObject) {
        motionSettings = updatedAnalyticSettingsArray.value.find((setting) => {
          return setting.id === ANALYTIC_TYPE.MOTION_DETECTION;
        });
        if (motionSettings) {
          motionObject = {
            type: motionSettings.id as AnalyticsType,
            enabled: motionSettings.performedOn !== PERFORMED_ON.DISABLED,
            configuration:
              motionSettings.selectedConfigurationIndex !== undefined && motionSettings.configuration?.length
                ? motionSettings.configuration[motionSettings.selectedConfigurationIndex]
                : undefined,
          };
          modifiedSettings.push(motionObject);
        }
      }

      const motionDetectionRegions = motionObject?.configuration?.regions;
      modifiedSettings = modifiedSettings.map((setting) => {
        if (
          motionDetectionRegions &&
          (setting.type === ANALYTIC_TYPE.PERSON_DETECTION || setting.type === ANALYTIC_TYPE.VEHICLE_DETECTION)
        ) {
          const regions = setting.configuration?.regions;
          if (regions?.length) {
            const updatedRegions = regions.map((region) => {
              if (Number(region?.motionRegionId)) {
                const motionRegIndex = motionDetectionRegions.findIndex((reg) => {
                  return reg.id === region?.motionRegionId;
                });
                if (motionRegIndex > -1) {
                  const name =
                    setting.type === ANALYTIC_TYPE.PERSON_DETECTION
                      ? `Person detection: ${region.name}`
                      : setting.type === ANALYTIC_TYPE.VEHICLE_DETECTION
                      ? `Vehicle detection: ${region.name}`
                      : region.name;
                  motionDetectionRegions[motionRegIndex].name = name;
                  motionDetectionRegions[motionRegIndex].polygon = region.polygon;
                  delete region.polygon;
                  delete region.name;
                }
                return region;
              } else {
                delete region.motionRegionId;
                return region;
              }
            });
            setting.configuration.regions = updatedRegions;
          }
          return setting;
        } else {
          if ([ANALYTIC_TYPE.VEHICLE_DETECTION, ANALYTIC_TYPE.PERSON_DETECTION].includes(setting.type)) {
            setting?.configuration?.regions?.map((region) => {
              if (!Number(region.motionRegionId)) {
                delete region.motionRegionId;
              }
            });
          }

          return setting;
        }
      });
      if (motionDetectionRegions?.length) {
        modifiedSettings = modifiedSettings.map((setting) => {
          if (setting.type === ANALYTIC_TYPE.MOTION_DETECTION) {
            setting.configuration.regions = motionDetectionRegions;
            return setting;
          } else {
            return setting;
          }
        });
      }
    }

    return modifiedSettings;
  };

  function reset() {
    analyticSettings.value = [];
    updatedAnalyticSettingsArray.value = [];
    initialAnalyticSettingsArray.value = [];
    motionDetectionSelectedRegion.value = 0;
    selectedRegions.value = DEFAULT_SELECTED_REGIONS;
  }

  const isLoadingcameraTunnel = ref(false);
  const isCameraTunnelError = ref(false);

  async function getCameraTunnel(
    params: CameraTunnelParams,
    authToken: string,
    controller?: AbortController,
    cancelReq = false
  ) {
    try {
      isLoadingcameraTunnel.value = true;
      if (cancelReq && controller) {
        controller.abort();
      }

      const response = await api.getCameraTunnel(params, authToken, controller?.signal);

      cameraTunnelResult.value = response?.data ?? {};
    } catch (error) {
      cameraTunnelResult.value = null;
      isCameraTunnelError.value = true;
    } finally {
      isLoadingcameraTunnel.value = false;
    }
  }

  watch(
    () => analyticSettings.value,
    (newValue) => {
      if (shouldUpdateMotionRegionID.value) {
        const motionDetection = newValue.find((item) => item.type === ANALYTIC_TYPE.MOTION_DETECTION);
        const personDetection = newValue.find((item) => item.type === ANALYTIC_TYPE.PERSON_DETECTION);
        const vehicleDetection = newValue.find((item) => item.type === ANALYTIC_TYPE.VEHICLE_DETECTION);

        if (motionDetection) {
          const motionRegions = motionDetection.configuration?.regions;

          const settings = updatedAnalyticSettingsArray.value;

          const personSettings = settings.find((item) => item.id === ANALYTIC_TYPE.PERSON_DETECTION);
          const vehicleSettings = settings.find((item) => item.id === ANALYTIC_TYPE.VEHICLE_DETECTION);
          const motionSettings = settings.find((item) => item.id === ANALYTIC_TYPE.MOTION_DETECTION);

          if (motionRegions) {
            const personRegions = [];
            const vehicleRegions = [];
            motionRegions.forEach((region) => {
              if (region.name.includes('Person detection')) {
                personRegions.push(region);
              } else if (region.name.includes('Vehicle detection')) {
                vehicleRegions.push(region);
              }
            });

            if (personSettings?.performedOn === PERFORMED_ON.DISABLED) {
              updatedAnalyticSettingsArray.value = updatedAnalyticSettingsArray.value.map((analyticSetting) => {
                if (analyticSetting.id === ANALYTIC_TYPE.PERSON_DETECTION) {
                  analyticSetting.configuration = undefined;
                  analyticSetting.configurationType = undefined;
                  analyticSetting.selectedConfigurationIndex = undefined;
                  return analyticSetting;
                }
                return analyticSetting;
              });
            } else {
              const personRegionSettings =
                personSettings?.configuration &&
                personSettings?.configuration[personSettings.selectedConfigurationIndex]?.regions;
              if (personRegionSettings) {
                updatedAnalyticSettingsArray.value = updatedAnalyticSettingsArray.value.map((analyticSetting) => {
                  if (analyticSetting.id === ANALYTIC_TYPE.PERSON_DETECTION) {
                    const updatedRegions = personDetection.configuration?.regions.map((region) => {
                      const correspondingMotionRegion = motionRegions.find(
                        (motionRegion) => motionRegion.id === region.motionRegionId
                      );

                      return {
                        motionRegionId: region.motionRegionId,
                        includeStationaryObjects: region.includeStationaryObjects,
                        name: correspondingMotionRegion.name.split('Person detection:')[1].trim(),
                        polygon: correspondingMotionRegion.polygon,
                      };
                    });

                    const updatedConfiguration = { ...personDetection.configuration, regions: updatedRegions };

                    analyticSetting.configuration[analyticSetting.selectedConfigurationIndex] = updatedConfiguration;

                    return analyticSetting;
                  }

                  return analyticSetting;
                });
              }
            }

            if (vehicleSettings?.performedOn === PERFORMED_ON.DISABLED) {
              updatedAnalyticSettingsArray.value = updatedAnalyticSettingsArray.value.map((analyticSetting) => {
                if (analyticSetting.id === ANALYTIC_TYPE.VEHICLE_DETECTION) {
                  analyticSetting.configuration = undefined;
                  analyticSetting.configurationType = undefined;
                  analyticSetting.selectedConfigurationIndex = undefined;
                  return analyticSetting;
                }
                return analyticSetting;
              });
            } else {
              const vehicleRegionSettings =
                vehicleSettings?.configuration &&
                vehicleSettings?.configuration[vehicleSettings.selectedConfigurationIndex]?.regions;

              if (vehicleRegionSettings) {
                updatedAnalyticSettingsArray.value = updatedAnalyticSettingsArray.value.map((analyticSetting) => {
                  if (analyticSetting.id === ANALYTIC_TYPE.VEHICLE_DETECTION) {
                    const updatedRegions = vehicleDetection.configuration?.regions.map((region) => {
                      const correspondingMotionRegion = motionRegions.find(
                        (motionRegion) => motionRegion.id === region.motionRegionId
                      );

                      return {
                        motionRegionId: region.motionRegionId,
                        includeStationaryObjects: region.includeStationaryObjects,
                        name: correspondingMotionRegion.name.split('Vehicle detection:')[1].trim(),
                        polygon: correspondingMotionRegion.polygon,
                      };
                    });

                    const updatedConfiguration = { ...vehicleDetection.configuration, regions: updatedRegions };

                    analyticSetting.configuration[analyticSetting.selectedConfigurationIndex] = updatedConfiguration;

                    return analyticSetting;
                  }

                  return analyticSetting;
                });
              }
            }

            if (motionSettings?.performedOn === PERFORMED_ON.DISABLED) {
              motionSettings.performedOn = PERFORMED_ON.BRIDGE;
              motionSettings.configuration = [motionDetection.configuration];
              motionSettings.selectedConfigurationIndex = 0;
              motionSettings.configurationType = 'settings';
            } else if (motionSettings?.performedOn === PERFORMED_ON.BRIDGE) {
              motionSettings.configuration = [motionDetection.configuration];
            }
          } else if (!motionDetection.enabled) {
            if (
              personSettings?.configuration?.[personSettings.selectedConfigurationIndex].regions?.length &&
              personSettings.performedOn !== PERFORMED_ON.DISABLED
            ) {
              personSettings.configuration[personSettings.selectedConfigurationIndex].regions = [];
            }
            if (
              vehicleSettings?.configuration?.[vehicleSettings.selectedConfigurationIndex].regions?.length &&
              vehicleSettings.performedOn !== PERFORMED_ON.DISABLED
            ) {
              vehicleSettings.configuration[vehicleSettings.selectedConfigurationIndex].regions = [];
            }

            motionSettings.configuration = [];
          }
        }
      }
      shouldUpdateMotionRegionID.value = false;
    },
    { deep: true }
  );
  return {
    analyticSettings,
    bridgeBandWidth,
    bulkUpdateAnalyticSettings,
    getAnalyticsSettings,
    getBridgeBandWidth,
    isLoadingBridgeSettingsData,
    isPersonUpdatedFromMotion,
    isVehicleUpdatedFromMotion,
    loading,
    motionDetectionSelectedRegion,
    objectDetectionConfig,
    reset,
    selectedRegions,
    showHiddenAnalytics,
    showReferenceObject,
    referenceObjectPolygon,
    referenceObjectType,
    initialAnalyticSettingsArray,
    updatedAnalyticSettingsArray,
    selectedRegionCommon,
    vehicleDetectionSelectedRegion,
    getCameraTunnel,
    cameraTunnelResult,
    isLoadingcameraTunnel,
    isCameraTunnelError,
    showLPRBridgeAdvanceSettings,
    isSavedLPRBridgeOption,
    isSelectedLPRBridgeOption,
    selectedMotionDetectionId,
    shouldUpdateMotionRegionID,
    settingsRequiringROI,
    settingsWithoutROI,
    showReferenceAnalyticsConfig,
    shouldShowLPRCloudWarningMessage,
    selectedPersonDetectionId,
    selectedVehicleDetectionId,
    selectedGunDetectionId,
    fleetCodeCameraDropdownRect,
  };
});
